import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://namal-chamodya.space';
const DEFAULT_IMAGE = `${SITE_URL}/dp.png`;
const SITE_NAME = 'Namal Chamodya Hub';

/**
 * Reusable SEO component — injects <title>, meta tags, OG, Twitter, and JSON-LD per page.
 *
 * @param {string} title       — Page title (will append " | Namal Chamodya" if not included)
 * @param {string} description — Page meta description (150-160 chars ideal)
 * @param {string} keywords    — Comma-separated keywords
 * @param {string} canonical   — Canonical path, e.g. "/courses"
 * @param {string} ogImage     — Open Graph image URL (defaults to dp.png)
 * @param {string} ogType      — Open Graph type (defaults to "website")
 * @param {object|array} jsonLd — JSON-LD structured data object(s) for rich snippets
 */
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  jsonLd,
}) => {
  const fullTitle = title?.includes('Namal Chamodya') ? title : `${title} | Namal Chamodya`;
  const fullCanonical = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const image = ogImage || DEFAULT_IMAGE;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:domain" content="namal-chamodya.space" />
      <meta property="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
